import { USDV_DECIMALS, USDV_MINT } from "@shared/tokens/usdv";
import { BigDecimal } from "@solomon-labs/bigdecimal";

import type { JupiterUltraQuoteResponse } from "./jupiterPayload";

// Temporary display price for current USDv until Jupiter supplies its USD valuation.
const USDV_FALLBACK_USD_PRICE = new BigDecimal(1);

function isPositiveUsdValue(value: number | null | undefined): value is number {
    return typeof value === "number" && Number.isFinite(value) && value > 0;
}

interface SwapUsdValueInput {
    mint: string;
    amount: string;
    quotedUsdValue?: number;
    quotedRawAmount?: string;
    spotUsdPrice: number | null;
}

export function getSwapUsdValue(input: SwapUsdValueInput): BigDecimal {
    if (isPositiveUsdValue(input.quotedUsdValue)) {
        return new BigDecimal(input.quotedUsdValue);
    }

    try {
        const isUsdv = input.mint === USDV_MINT;
        // Use the net quote amount before the form rounds it for display.
        const amount =
            isUsdv && input.quotedRawAmount !== undefined
                ? new BigDecimal(input.quotedRawAmount).divide(BigDecimal.pow10(USDV_DECIMALS))
                : new BigDecimal(input.amount || "0");
        if (!amount.gt(0)) return new BigDecimal(0);

        if (isUsdv) return amount.multiply(USDV_FALLBACK_USD_PRICE);
        if (isPositiveUsdValue(input.spotUsdPrice)) return amount.multiply(input.spotUsdPrice);
    } catch {
        // Empty or incomplete form input has no display value.
    }

    return new BigDecimal(0);
}

export function getQuotePriceImpact(quote: JupiterUltraQuoteResponse | undefined): number | undefined {
    if (
        !quote ||
        !isPositiveUsdValue(quote.inUsdValue) ||
        !isPositiveUsdValue(quote.outUsdValue) ||
        !Number.isFinite(quote.priceImpact)
    ) {
        return undefined;
    }

    const feeBps = quote.feeBps;
    if (!Number.isFinite(feeBps) || feeBps < 0 || feeBps >= 10000) return undefined;

    // Jupiter's net output includes the total swap fee, including gasless rent
    // recoup. Remove that fee once before comparing market USD values. Using
    // actual quote valuations also avoids assuming stablecoins always trade at $1.
    // Positive means an adverse rate; negative means a price improvement.
    const retainedFraction = new BigDecimal(1).subtract(new BigDecimal(feeBps).divide(10000));
    const outputBeforeFee = new BigDecimal(quote.outUsdValue).divide(retainedFraction);
    return Number(new BigDecimal(1).subtract(outputBeforeFee.divide(quote.inUsdValue)).multiply(100).toString());
}
